import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ApiService } from '../services/api.service';
import { HumanizeEnumPipe } from '../pipes/humanize-enum.pipe';

interface PurchaseOrder {
  id: number;
  number?: string;
  date?: Date;
  vendorId?: number;
  vendorName?: string;
  qboId?: number;
  status?: string;
  totalAmount?: number;
  salesTax?: number;
  shipFee?: number;
  handlingFee?: number;
}

@Component({
  standalone: true,
  selector: 'app-purchaseorders',
  templateUrl: './purchaseorders.component.html',
  styleUrl: './purchaseorders.component.css',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    HumanizeEnumPipe
  ]
})
export class PurchaseOrdersComponent implements OnInit {
  dataSource = new MatTableDataSource<PurchaseOrder>([]);
  displayedColumns: string[] = ['number', 'date', 'vendorName', 'status', 'totalAmount', 'copy'];
  loading: boolean = true;
  error: string | null = null;
  copyingOrderId: number | null = null;

  @ViewChild(MatSort) set matSort(s: MatSort) {
    if (s) { this.dataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    if (p) { this.dataSource.paginator = p; }
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPurchaseOrders();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  loadPurchaseOrders(): void {
    this.loading = true;
    this.error = null;
    
    this.apiService.get<any>('purchaseorder/all').subscribe({
      next: (data) => {
        let ordersArray: any[];
        if (Array.isArray(data)) {
          ordersArray = data;
        } else if (data && data.purchaseOrders) {
          ordersArray = data.purchaseOrders;
        } else if (data && data.data) {
          ordersArray = data.data;
        } else if (data && data.value) {
          ordersArray = data.value;
        } else {
          ordersArray = [];
        }
        
        const purchaseOrders = ordersArray.map(po => ({
          id: po.id || po.Id,
          number: po.number || po.Number,
          date: po.date || po.Date,
          vendorId: po.vendorId || po.VendorId,
          vendorName: (po.vendor?.name || po.Vendor?.Name || po.vendorName || po.VendorName || ''),
          qboId: po.qboId || po.QBO_Id,
          status: po.status || po.Status,
          totalAmount: po.totalAmount || po.TotalAmount || 0,
          salesTax: po.salesTax || po.SalesTax,
          shipFee: po.shipFee || po.ShipFee,
          handlingFee: po.handlingFee || po.HandlingFee
        }));
        
        this.dataSource.data = purchaseOrders;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading purchase orders:', err);
        
        if (err.status === 0) {
          this.error = 'Cannot connect to API. Make sure the Web API is running.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log out and log in again.';
        } else if (err.status === 404) {
          this.error = 'API endpoint not found.';
        } else {
          this.error = `Failed to load purchase orders: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  createNewOrder(): void {
    this.router.navigate(['/purchase-orders/new']);
  }

  viewOrder(order: PurchaseOrder): void {
    this.router.navigate(['/purchase-orders', order.id]);
  }

  editOrder(order: PurchaseOrder): void {
    this.router.navigate(['/purchase-orders', order.id]);
  }

  copyOrder(order: PurchaseOrder, event: Event): void {
    event.stopPropagation();
    this.copyingOrderId = order.id;

    this.apiService.get<any>(`purchaseorder/${order.id}`).subscribe({
      next: (data) => {
        this.copyingOrderId = null;
        this.router.navigate(['/purchase-orders/new'], { state: { prefill: data } });
      },
      error: (err) => {
        console.error('Error copying purchase order:', err);
        this.error = `Failed to copy purchase order: ${err.message || 'Unknown error'}`;
        this.copyingOrderId = null;
        this.cdr.detectChanges();
      }
    });
  }
}
